/**
 * 图像编解码的对外接口（M1-14 / DESIGN.md §6.4）。
 *
 * **解码优先在 Worker 里跑**，并且接受 `AbortSignal`：一张 8000×8000 的 JPEG
 * 解码要几百毫秒，放主线程会直接卡住界面（CLAUDE.md 代码约定：所有耗时 > 16ms
 * 的操作必须接受 AbortSignal）。
 *
 * 取消的语义是**真的停下来**：wasm 里的 libpng/libjpeg 是同步循环，
 * 没有中途返回的口子，所以取消 = 终止 Worker。这比"标记一个 flag 然后
 * 等它自己跑完"诚实——用户点了取消就该立刻释放 CPU。
 *
 * **Worker 起不来时退回主线程**（`WorkerUnavailableError`，见下）：Safari 的
 * module worker 在页面开了 `Cross-Origin-Embedder-Policy` 时会整体拒绝创建
 * （即便响应头齐全，这是 WebKit 对 spec 过度严格的已知实现），重试没有用。
 * 退到主线程直接跑 wasm 是唯一的出路——牺牲这一次的"不卡界面"，好过图片
 * 直接打不开。给 worker 的请求体故意每次都拷一份（`data.slice()`）而不是
 * 直接转移原 buffer：转移是 `postMessage` 调用那一刻同步生效的，哪怕 worker
 * 随后加载失败，原 buffer 也已经摘断——退回主线程时如果还想用原始 `data`，
 * 它就必须是完好的。
 */
import { CodecInstance, type DecodedImage } from './wasm';

export {
  CodecError,
  CodecFailure,
  CodecInstance,
  DECODE_RESULT_SIZE,
  loadCodecModule,
  setCodecModuleForTest,
  type CodecErrorCode,
  type DecodedImage,
} from './wasm';

/** 支持的图像格式。 */
export type ImageFormat = 'png' | 'jpeg';

/**
 * 8/16 位统一到 RGBA8。16 位是小端序 Uint16，取高 8 位即正确的舍入近似。
 * 引擎/图层只认 RGBA8（CLAUDE.md 规则 6：像素数据只归 TileStore 管，
 * TileStore 是 RGBA8），解码结果一律要先过这一步。
 */
export function decodedImageToRgba8(image: DecodedImage): Uint8Array {
  if (image.bitDepth === 8) return image.pixels;
  const wide = new Uint16Array(
    image.pixels.buffer,
    image.pixels.byteOffset,
    image.pixels.byteLength / 2,
  );
  const out = new Uint8Array(wide.length);
  for (let i = 0; i < wide.length; i++) out[i] = wide[i]! >> 8;
  return out;
}

/** 按文件头判断格式。**不看扩展名**——扩展名是用户可以随便改的。 */
export function sniffFormat(data: Uint8Array): ImageFormat | null {
  if (data.length >= 8) {
    const png = [137, 80, 78, 71, 13, 10, 26, 10];
    if (png.every((b, i) => data[i] === b)) return 'png';
  }
  if (data.length >= 3 && data[0] === 0xff && data[1] === 0xd8 && data[2] === 0xff) {
    return 'jpeg';
  }
  return null;
}

export class DecodeAbortedError extends Error {
  constructor() {
    super('解码已取消');
    this.name = 'DecodeAbortedError';
  }
}

export class UnsupportedFormatError extends Error {
  constructor(hint: string) {
    super(`无法识别的图像格式：${hint}`);
    this.name = 'UnsupportedFormatError';
  }
}

/**
 * Worker 本身起不来（区别于「worker 跑起来了但 wasm 解码/编码失败」）。
 *
 * 已知触发场景：Safari 的 dev-mode module worker 在 `Cross-Origin-Embedder-Policy`
 * 打开时会整体拒绝加载（即使响应头齐全——这是 WebKit 对 spec 的过度严格实现，
 * 生产构建走 IIFE worker 不受影响，只有 `vite dev` 下的原生 ESM worker 会中招）。
 * 这类失败重试也没用，唯一的出路是退回主线程直接跑 wasm——见 `runOnMainThread`。
 */
class WorkerUnavailableError extends Error {}

interface DecodeRequest {
  readonly id: number;
  readonly kind: 'decode';
  readonly format: ImageFormat;
  readonly data: ArrayBuffer;
}

interface EncodeRequest {
  readonly id: number;
  readonly kind: 'encode';
  readonly format: ImageFormat;
  readonly pixels: ArrayBuffer;
  readonly width: number;
  readonly height: number;
  readonly bitDepth?: number;
  readonly quality?: number;
  readonly progressive?: boolean;
  readonly icc?: ArrayBuffer | null;
  readonly exif?: ArrayBuffer | null;
}

export type CodecRequest = DecodeRequest | EncodeRequest;

export type CodecResponse =
  | { readonly id: number; readonly ok: true; readonly kind: 'decode'; readonly image: DecodedImage }
  | { readonly id: number; readonly ok: true; readonly kind: 'encode'; readonly bytes: Uint8Array }
  | { readonly id: number; readonly ok: false; readonly message: string; readonly code?: number };

export interface DecodeOptions {
  readonly signal?: AbortSignal;
}

/**
 * 在 Worker 中解码。
 *
 * 每次调用起一个 Worker 并在结束后终止它。听起来浪费，但对解码这种
 * 一次性的重任务是对的：Worker 启动约 1–2ms，而复用会让 wasm 的线性内存
 * 在解过大图之后再也降不回来（wasm 内存只增不减）。
 */
export async function decodeImage(
  data: Uint8Array,
  options: DecodeOptions = {},
): Promise<DecodedImage> {
  const format = sniffFormat(data);
  if (format === null) {
    throw new UnsupportedFormatError(describeHead(data));
  }
  try {
    return await runInWorker<DecodedImage>(
      {
        id: 1,
        kind: 'decode',
        format,
        // 必须拷贝而不是直接转移 data.buffer：`postMessage` 的 transfer 是
        // **调用那一刻**在主线程同步生效的——哪怕 worker 后面加载失败
        // （Safari 的 module worker + COEP 已知限制），传出去的 buffer 也已经
        // 被摘断了。之前这里直接 `toTransferable(data)` 复用 `data.buffer`，
        // 结果就是 worker 起不来退回主线程兜底时，`data` 早就是一个长度归零
        // 的失效视图——decodePng 静默地在空数据上跑了一轮，既不抛错也不挂起，
        // 只是什么都没解出来。多一次拷贝换来兜底路径能拿到完整数据，值得。
        data: data.slice().buffer,
      },
      options.signal,
    );
  } catch (error) {
    if (!(error instanceof WorkerUnavailableError)) throw error;
    if (options.signal?.aborted === true) throw new DecodeAbortedError();
    const codec = await CodecInstance.create();
    return format === 'png' ? codec.decodePng(data) : codec.decodeJpeg(data);
  }
}

export interface EncodeOptions extends DecodeOptions {
  readonly width: number;
  readonly height: number;
  readonly bitDepth?: 8 | 16;
  readonly quality?: number;
  readonly progressive?: boolean;
  readonly icc?: Uint8Array | null;
  readonly exif?: Uint8Array | null;
}

export async function encodeImage(
  format: ImageFormat,
  pixels: Uint8Array,
  options: EncodeOptions,
): Promise<Uint8Array> {
  try {
    return await runInWorker<Uint8Array>(
      {
        id: 1,
        kind: 'encode',
        format,
        // 同 decodeImage：拷贝而不是复用 buffer，见那边的注释
        pixels: pixels.slice().buffer,
        width: options.width,
        height: options.height,
        ...(options.bitDepth === undefined ? {} : { bitDepth: options.bitDepth }),
        ...(options.quality === undefined ? {} : { quality: options.quality }),
        ...(options.progressive === undefined ? {} : { progressive: options.progressive }),
        icc: options.icc == null ? null : options.icc.slice().buffer,
        exif: options.exif == null ? null : options.exif.slice().buffer,
      },
      options.signal,
    );
  } catch (error) {
    if (!(error instanceof WorkerUnavailableError)) throw error;
    if (options.signal?.aborted === true) throw new DecodeAbortedError();
    const codec = await CodecInstance.create();
    return format === 'png'
      ? codec.encodePng({
          pixels,
          width: options.width,
          height: options.height,
          bitDepth: options.bitDepth === 16 ? 16 : 8,
          icc: options.icc ?? null,
        })
      : codec.encodeJpeg({
          pixels,
          width: options.width,
          height: options.height,
          ...(options.quality === undefined ? {} : { quality: options.quality }),
          ...(options.progressive === undefined ? {} : { progressive: options.progressive }),
          icc: options.icc ?? null,
          exif: options.exif ?? null,
        });
  }
}

/** 文件头的十六进制摘要，用于报错时给出线索。 */
function describeHead(data: Uint8Array): string {
  return Array.from(data.slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

/** Worker 工厂。测试可以替换成同线程实现。 */
let workerFactory: () => Worker = () =>
  new Worker(new URL('./codec-worker.ts', import.meta.url), { type: 'module' });

export function setCodecWorkerFactory(factory: (() => Worker) | null): void {
  workerFactory =
    factory ??
    (() => new Worker(new URL('./codec-worker.ts', import.meta.url), { type: 'module' }));
}

function runInWorker<T>(request: CodecRequest, signal?: AbortSignal): Promise<T> {
  if (signal?.aborted === true) {
    return Promise.reject(new DecodeAbortedError());
  }

  return new Promise<T>((resolve, reject) => {
    let worker: Worker;
    try {
      worker = workerFactory();
    } catch (error) {
      reject(new WorkerUnavailableError(error instanceof Error ? error.message : String(error)));
      return;
    }
    let settled = false;

    const cleanup = (): void => {
      signal?.removeEventListener('abort', onAbort);
      worker.terminate();
    };

    function onAbort(): void {
      if (settled) return;
      settled = true;
      // 直接终止：wasm 里的解码是同步循环，没有中途返回的口子。
      // 只置个 flag 等它跑完，等于取消了个寂寞
      cleanup();
      reject(new DecodeAbortedError());
    }

    signal?.addEventListener('abort', onAbort, { once: true });

    worker.onmessage = (event: MessageEvent<CodecResponse>): void => {
      if (settled) return;
      settled = true;
      const response = event.data;
      cleanup();
      if (!response.ok) {
        reject(new CodecWorkerError(response.message, response.code));
        return;
      }
      resolve((response.kind === 'decode' ? response.image : response.bytes) as T);
    };

    worker.onerror = (event: ErrorEvent): void => {
      if (settled) return;
      settled = true;
      cleanup();
      // codec-worker.ts 内部把所有异常都 catch 成了正常的 onmessage 响应
      // （见该文件），所以真正走到 onerror 的只有「worker 没跑起来」这一种
      // 情况（脚本加载被拦、语法错误等）——统一当作 WorkerUnavailableError，
      // 让调用方退回主线程重跑，而不是把一次环境限制误判成解码本身失败
      reject(new WorkerUnavailableError(event.message));
    };

    const transfer: Transferable[] = [];
    if (request.kind === 'decode') transfer.push(request.data);
    else {
      transfer.push(request.pixels);
      if (request.icc != null) transfer.push(request.icc);
      if (request.exif != null) transfer.push(request.exif);
    }
    worker.postMessage(request, transfer);
  });
}

export class CodecWorkerError extends Error {
  constructor(
    message: string,
    readonly code?: number,
  ) {
    super(message);
    this.name = 'CodecWorkerError';
  }
}
